import { Injectable, inject, Injector, runInInjectionContext } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  collectionData
} from '@angular/fire/firestore';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { map, tap, catchError, shareReplay, take, switchMap } from 'rxjs/operators';
import { Auth, authState } from '@angular/fire/auth';
import { TranslocoService } from '@jsverse/transloco';
import { Leader, LeaderCategory, LeaderSummary, LeaderTranslation } from '../models/leader.model';
import { CacheService } from './cache.service';

@Injectable({ providedIn: 'root' })
export class LeaderService {
  private firestore = inject(Firestore);
  private fbAuth = inject(Auth);
  private injector = inject(Injector);
  private cacheService = inject(CacheService);
  private transloco = inject(TranslocoService);
  private leadersCollection = collection(this.firestore, 'leaders');

  private get lang(): string {
    return this.transloco.getActiveLang() ?? 'es';
  }

  // Inline translations schema: doc.translations[lang].{name,description,biography}
  // Falls back to 'en' then raw doc fields so cards never show empty name.
  private applyTranslations(leader: Leader): Leader {
    const t: LeaderTranslation | undefined =
      leader.translations?.[this.lang] ?? leader.translations?.['en'];
    if (!t) return leader;
    return {
      ...leader,
      name:        t.name        || leader.name,
      description: t.description || leader.description,
      biography:   t.biography   || leader.biography,
    };
  }

  /**
   * Merge a raw base doc + single-language translation into a Leader. Used by
   * the share-link flow (content from the `getSharedContent` callable).
   */
  buildLeader(id: string, base: Record<string, any>, transl: Record<string, any> | null): Leader {
    const leader = { id, ...base } as Leader;
    const t = transl as LeaderTranslation | null;
    if (!t) return leader;
    return {
      ...leader,
      name:        t.name        || leader.name,
      description: t.description || leader.description,
      biography:   t.biography   || leader.biography,
    };
  }

  // Fetch the translations subcollection (leaders/{id}/translations/{lang})
  // — name/biography/description live ONLY here, not on the root doc.
  // Returned as an inline map so applyTranslations + the raw cache keep working
  // unchanged and language switches never refetch.
  private async fetchTranslations(leaderId: string): Promise<Record<string, LeaderTranslation>> {
    const snap = await runInInjectionContext(this.injector, () =>
      getDocs(collection(this.firestore, 'leaders', leaderId, 'translations'))
    );
    const out: Record<string, LeaderTranslation> = {};
    snap.forEach(t => { out[t.id] = t.data() as LeaderTranslation; });
    return out;
  }

  // Map raw docs → Leader[], hydrating each with its translations subcollection.
  private async hydrateLeaders(docs: any[]): Promise<Leader[]> {
    return Promise.all(docs.map(async d => {
      const base = { id: d.id, ...d.data() } as Leader;
      // Retrocompat: si el doc ya trae translations inline, no leer la subcol.
      if (!base.translations || Object.keys(base.translations).length === 0) {
        base.translations = await this.fetchTranslations(d.id);
      }
      return base;
    }));
  }

  // Cache stores RAW docs (translations object intact). Translations are applied
  // at read time so language switches always return the correct locale without
  // invalidating the cache or re-fetching from Firestore.
  private rawCache: Leader[] = [];
  private cacheExpiry: number = 0;
  private readonly CACHE_DURATION_MS = 5 * 60 * 1000;

  getAllLeaders(): Observable<Leader[]> {
    if (this.isCacheValid()) {
      console.log('[LeaderService] getAllLeaders - returning from cache:', this.rawCache.length, 'leaders');
      return of(this.rawCache.map(l => this.applyTranslations(l)));
    }

    console.log('[LeaderService] getAllLeaders - fetching from Firestore...');
    const q = query(this.leadersCollection);
    return authState(this.fbAuth).pipe(
      take(1),
      switchMap(() => from(runInInjectionContext(this.injector, () => getDocs(q)))),
      switchMap(snapshot => {
        console.log('[LeaderService] Firestore snapshot size:', snapshot.size, 'empty:', snapshot.empty);
        return from(this.hydrateLeaders(snapshot.docs));
      }),
      tap(raw => {
        this.rawCache = raw;
        this.cacheExpiry = Date.now() + this.CACHE_DURATION_MS;
        console.log('[LeaderService] loaded', raw.length, 'leaders from Firestore');
      }),
      map(raw => raw.map(l => this.applyTranslations(l))),
      catchError(err => {
        console.error('[LeaderService] getAllLeaders error:', err);
        return of([]);
      })
    );
  }

  getLeaderById(id: string): Observable<Leader | null> {
    const cached = this.rawCache.find(l => l.id === id);
    if (cached) return of(this.applyTranslations(cached));

    const docRef = doc(this.firestore, `leaders/${id}`);
    return from(runInInjectionContext(this.injector, () => getDoc(docRef))).pipe(
      switchMap(snap => {
        if (!snap.exists()) return of(null);
        return from(this.hydrateLeaders([snap])).pipe(
          map(([leader]) => this.applyTranslations(leader))
        );
      }),
      catchError(() => of(null))
    );
  }

  getLeadersByCategory(category: LeaderCategory): Observable<Leader[]> {
    if (this.rawCache.length > 0) {
      return of(this.rawCache.filter(l => l.category === category).map(l => this.applyTranslations(l)));
    }

    const q = query(this.leadersCollection, where('category', '==', category));
    return from(runInInjectionContext(this.injector, () => getDocs(q))).pipe(
      switchMap(snap => from(this.hydrateLeaders(snap.docs))),
      map(raw => raw.map(l => this.applyTranslations(l)))
    );
  }

  getLeadersByCountry(country: string): Observable<Leader[]> {
    if (this.rawCache.length > 0) {
      return of(this.rawCache.filter(l => l.country.toLowerCase() === country.toLowerCase()).map(l => this.applyTranslations(l)));
    }

    const q = query(this.leadersCollection, where('country', '==', country));
    return from(runInInjectionContext(this.injector, () => getDocs(q))).pipe(
      switchMap(snap => from(this.hydrateLeaders(snap.docs))),
      map(raw => raw.map(l => this.applyTranslations(l)))
    );
  }

  getLeadersByPeriod(period: string): Observable<Leader[]> {
    if (this.rawCache.length > 0) {
      return of(this.rawCache.filter(l => l.period === period).map(l => this.applyTranslations(l)));
    }

    const q = query(this.leadersCollection, where('period', '==', period));
    return from(runInInjectionContext(this.injector, () => getDocs(q))).pipe(
      switchMap(snap => from(this.hydrateLeaders(snap.docs))),
      map(raw => raw.map(l => this.applyTranslations(l)))
    );
  }

  searchLeaders(searchQuery: string): Observable<Leader[]> {
    const term = searchQuery.toLowerCase();
    return this.getAllLeaders().pipe(
      map(leaders => leaders.filter(l =>
        l.name.toLowerCase().includes(term) ||
        l.country.toLowerCase().includes(term) ||
        l.period.toLowerCase().includes(term)
      ))
    );
  }

  getFeaturedLeaders(): Observable<Leader[]> {
    if (this.rawCache.length > 0) {
      return of([...this.rawCache].sort((a, b) =>
        this._toMs(b.createdAt) - this._toMs(a.createdAt)
      ).slice(0, 8).map(l => this.applyTranslations(l)));
    }

    const q = query(this.leadersCollection, orderBy('createdAt', 'desc'), limit(8));
    return from(runInInjectionContext(this.injector, () => getDocs(q))).pipe(
      switchMap(snap => from(this.hydrateLeaders(snap.docs))),
      map(raw => raw.map(l => this.applyTranslations(l))),
      catchError(() => of([]))
    );
  }

  getRecentlyAddedLeaders(): Observable<Leader[]> {
    if (this.rawCache.length > 0) {
      return of([...this.rawCache].sort((a, b) =>
        this._toMs(b.createdAt) - this._toMs(a.createdAt)
      ).slice(0, 20).map(l => this.applyTranslations(l)));
    }

    const q = query(this.leadersCollection, orderBy('createdAt', 'desc'), limit(20));
    return from(runInInjectionContext(this.injector, () => getDocs(q))).pipe(
      switchMap(snap => from(this.hydrateLeaders(snap.docs))),
      map(raw => raw.map(l => this.applyTranslations(l))),
      catchError(() => of([]))
    );
  }

  getPopularLeaders(): Observable<Leader[]> {
    if (this.rawCache.length > 0) {
      return of([...this.rawCache].sort((a, b) =>
        (b.viewCount ?? 0) - (a.viewCount ?? 0)
      ).slice(0, 10).map(l => this.applyTranslations(l)));
    }

    const q = query(this.leadersCollection, orderBy('viewCount', 'desc'), limit(10));
    return from(runInInjectionContext(this.injector, () => getDocs(q))).pipe(
      switchMap(snap => from(this.hydrateLeaders(snap.docs))),
      map(raw => raw.map(l => this.applyTranslations(l))),
      catchError(() => of([]))
    );
  }

  invalidateCache(): void {
    this.rawCache = [];
    this.cacheExpiry = 0;
    this.cacheService.invalidatePrefix('leaders:');
  }

  private isCacheValid(): boolean {
    return this.rawCache.length > 0 && Date.now() < this.cacheExpiry;
  }

  /**
   * Convierte un campo `createdAt` a ms, sea Firestore Timestamp o Date nativo.
   * Firestore Timestamp tiene `.seconds` (número); Date nativo tiene `.getTime()`.
   */
  private _toMs(date: any): number {
    if (!date) return 0;
    if (typeof date.seconds === 'number') return date.seconds * 1000; // Firestore Timestamp
    if (date instanceof Date) return date.getTime();
    return 0;
  }
}
